import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarCheck, Loader2, CheckCircle2, X, ArrowRight, ArrowLeft } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogClose,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { attributionPayload } from "@/access/tracker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", clinic_name: "", phone: "", clinic_type: "", emr: "", message: "" };

export const DemoFormFields = ({ onDone, showCloseButton = false }) => {
  const [form, setForm] = useState(EMPTY);
  const [step, setStep] = useState(0);            // 0 = details · 1 = follow-up · 2 = success
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Step 0 → 1 just advances the wizard locally
  const handleContinue = (e) => {
    e.preventDefault();
    setStep(1);
  };

  // Step 1 → submit the complete form (including follow-up question)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, attribution: attributionPayload() });
      setStep(2);
      toast.success("Demo request received! We'll be in touch shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  /* ────────────── STEP 2 — SUCCESS ────────────── */
  if (step === 2) {
    return (
      <div className="py-8 text-center" data-testid="demo-form-success">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-500">
          <CheckCircle2 className="h-8 w-8" />
        </div>
        <h3 className="mt-5 font-display text-2xl font-bold">You&apos;re all set!</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          Thanks {form.name?.split(" ")[0] || "there"} — our team will reach out within one
          business day to schedule your personalized ClinicFirst demo.
        </p>
        {showCloseButton && (
          <button
            onClick={() => onDone?.()}
            data-testid="demo-form-close"
            className="mt-6 inline-flex items-center justify-center rounded-lg border border-border px-6 py-2.5 text-sm font-semibold hover:bg-secondary"
          >
            Done
          </button>
        )}
      </div>
    );
  }

  /* ────────────── STEP 1 — FOLLOW-UP QUESTION ────────────── */
  if (step === 1) {
    return (
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="demo-form-step2">
        <button
          type="button"
          onClick={() => setStep(0)}
          data-testid="demo-form-back"
          className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" />
          Back to details
        </button>

        <div className="rounded-2xl border border-violet-500/25 bg-gradient-to-br from-violet-500/[0.06] via-fuchsia-500/[0.03] to-transparent p-4 sm:p-5">
          <p className="inline-flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.18em] text-violet-700 dark:text-violet-300">
            <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-violet-500/20 text-[10px]">2</span>
            One last quick question
          </p>
          <h4 className="mt-2.5 font-display text-[17px] font-black leading-tight tracking-tight sm:text-[18.5px]">
            Is there anything you&apos;d like to discuss specifically on this call?
          </h4>
          <p className="mt-1.5 text-[12.5px] leading-relaxed text-muted-foreground">
            Optional — but if you share a focus area, we&apos;ll tailor the walkthrough to it before the call.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="msg2" className="sr-only">Anything specific to discuss?</Label>
          <Textarea
            id="msg2"
            data-testid="demo-input-message"
            value={form.message}
            onChange={(e) => update("message", e.target.value)}
            rows={5}
            placeholder="e.g. We're drowning in missed calls after hours · we want to switch from Mailchimp + Hootsuite · we want to see how Pulse rolls up our 3 locations · etc."
            autoFocus
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          data-testid="demo-form-submit"
          className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-violet-600 px-6 py-3.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 disabled:opacity-70"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarCheck className="h-4 w-4" />}
          {loading ? "Submitting..." : "Submit my demo request"}
        </button>

        <p className="text-center text-[11.5px] text-muted-foreground">
          You can skip — just hit submit. We&apos;ll still get back within a business day.
        </p>
      </form>
    );
  }

  /* ────────────── STEP 0 — CONTACT DETAILS ────────────── */
  return (
    <form onSubmit={handleContinue} className="space-y-4" data-testid="demo-form">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="name">Full name *</Label>
          <Input id="name" data-testid="demo-input-name" required value={form.name}
            onChange={(e) => update("name", e.target.value)} placeholder="Dr. Sarah Mitchell" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="clinic">Clinic name *</Label>
          <Input id="clinic" data-testid="demo-input-clinic" required value={form.clinic_name}
            onChange={(e) => update("clinic_name", e.target.value)} placeholder="Apex Physiotherapy" />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="email">Work email *</Label>
          <Input id="email" type="email" data-testid="demo-input-email" required value={form.email}
            onChange={(e) => update("email", e.target.value)} placeholder="sarah@clinic.com" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="phone">Phone *</Label>
          <Input id="phone" data-testid="demo-input-phone" required value={form.phone}
            onChange={(e) => update("phone", e.target.value)} placeholder="(613) 555-0199" />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label>Clinic type</Label>
          <Select value={form.clinic_type} onValueChange={(v) => update("clinic_type", v)}>
            <SelectTrigger data-testid="demo-select-type"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              {["Physiotherapy", "Chiropractic", "Massage Therapy", "Dental", "Dietetics", "Other"].map((o) => (
                <SelectItem key={o} value={o}>{o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Current EMR</Label>
          <Select value={form.emr} onValueChange={(v) => update("emr", v)}>
            <SelectTrigger data-testid="demo-select-emr"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              {["Jane App", "Juvonno", "Other", "None yet"].map((o) => (
                <SelectItem key={o} value={o}>{o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex items-start gap-2 rounded-lg border border-blue-500/25 bg-blue-500/[0.05] p-3 text-[12px] leading-snug text-foreground/85">
        <span className="mt-0.5 inline-flex h-4 w-4 flex-none items-center justify-center rounded-full bg-blue-500/15 text-blue-600 dark:text-blue-300 text-[10px] font-bold">i</span>
        <p>
          <span className="font-bold">Pricing.</span>{" "}
          <span className="text-muted-foreground">Base starts at <span className="font-semibold text-foreground">$297 / $497 per location</span>. Add-ons are quoted separately after a 20-min discovery call — no contact-us gates.</span>
        </p>
      </div>

      <button
        type="submit"
        data-testid="demo-form-continue"
        className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-violet-600 px-6 py-3.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5"
      >
        Continue
        <ArrowRight className="h-4 w-4" />
      </button>
      <p className="text-center text-[11.5px] text-muted-foreground">
        Step 1 of 2 · One more quick question after this.
      </p>
    </form>
  );
};

export const DemoForm = ({ open, onOpenChange }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent
      hideClose
      data-testid="demo-form-dialog"
      className="
        max-w-lg p-0 gap-0 max-h-[92vh] overflow-hidden
        sm:top-[50%] sm:translate-y-[-50%] sm:rounded-lg
        top-auto bottom-0 left-0 right-0 w-full translate-x-0 translate-y-0
        rounded-t-2xl rounded-b-none
        sm:left-[50%] sm:translate-x-[-50%] sm:bottom-auto sm:w-full sm:rounded-b-lg
        data-[state=closed]:slide-out-to-bottom-[2%] data-[state=open]:slide-in-from-bottom-[2%]
        sm:data-[state=closed]:slide-out-to-top-[48%] sm:data-[state=open]:slide-in-from-top-[48%]
      "
    >
      <DialogHeader className="sticky top-0 z-20 border-b border-border bg-card/95 px-5 py-4 backdrop-blur sm:px-6">
        <span aria-hidden className="absolute left-1/2 top-2 h-1 w-10 -translate-x-1/2 rounded-full bg-foreground/15 sm:hidden" />
        <DialogClose
          data-testid="demo-form-close-x"
          aria-label="Close"
          className="absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-full border border-border bg-background/80 text-foreground/70 transition-colors hover:bg-secondary hover:text-foreground focus:outline-none focus:ring-2 focus:ring-violet-400 sm:h-9 sm:w-9"
        >
          <X className="h-4 w-4" />
        </DialogClose>
        <DialogTitle className="font-display text-xl font-bold tracking-tight flex items-center gap-2 pr-10 pt-2 sm:pt-0 sm:text-2xl">
          <CalendarCheck className="h-5 w-5 text-violet-500" /> Book your demo
        </DialogTitle>
        <DialogDescription className="text-[12.5px] sm:text-sm">
          See Clinic OS in action. Plans start at $297/mo — no commitment.
        </DialogDescription>
      </DialogHeader>
      <div className="max-h-[calc(92vh-104px)] overflow-y-auto px-5 pb-6 pt-4 sm:max-h-[70vh] sm:px-6">
        <DemoFormFields showCloseButton onDone={() => onOpenChange(false)} />
      </div>
    </DialogContent>
  </Dialog>
);
